import { useEffect } from 'react';
import useSalesBotStore from '@/stores/SalesBotStore';
import useUserStore from '@/stores/UserStore';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { cn } from '@/lib/utils';
import {
  getAutopilotPrompt,
  getAutopilotSettings,
  manageSalesBot,
} from '@/hooks/sales-bot';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const tabs = [
  { value: 'prompt', label: 'Conversation Prompt', path: '/revio-autopilot' },
  {
    value: 'human-handover',
    label: 'Human Handover',
    path: '/revio-autopilot/human-handover',
  },
  { value: 'followup', label: 'Followup', path: '/revio-autopilot/followup' },
  { value: 'tagging', label: 'Tagging', path: '/revio-autopilot/tagging' },
  {
    value: 'spam-detection',
    label: 'Spam Detection',
    path: '/revio-autopilot/spam-detection',
  },
  {
    value: 'settings',
    label: 'Settings',
    path: '/revio-autopilot/settings',
  },
];

const AutopilotLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { salesBotEnabled, autoPilotFeatureEnabled } = useUserStore(
    useShallow((state) => ({
      salesBotEnabled: state.salesBotEnabled,
      autoPilotFeatureEnabled: state.autoPilotFeatureEnabled,
    }))
  );

  const {
    autopilotEnabled,
    setAutoPilotEnabled,
    updateSystemPrompt,
    updateAutopilotSettings,
  } = useSalesBotStore(
    useShallow((state) => ({
      autopilotEnabled: state.autopilotEnabled,
      setAutoPilotEnabled: state.setAutoPilotEnabled,
      updateSystemPrompt: state.updateSystemPrompt,
      updateAutopilotSettings: state.updateAutopilotSettings,
    }))
  );

  const getActiveTab = () => {
    const currentTab = tabs.find((tab) => tab.path === location.pathname);
    return currentTab ? currentTab.value : 'prompt';
  };

  const activeTab = getActiveTab();

  // Load all data from APIs on mount
  useEffect(() => {
    const loadAllData = async () => {
      try {
        const promptResponse = await getAutopilotPrompt();
        if (promptResponse && promptResponse.salesbot_system_prompt) {
          updateSystemPrompt(promptResponse.salesbot_system_prompt);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || 'Failed to load autopilot prompt'
        );
      }

      try {
        // Load settings
        const settingsResponse = await getAutopilotSettings();
        if (settingsResponse) {
          console.log('settingsResponse', settingsResponse);
          updateAutopilotSettings(settingsResponse.data);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || 'Failed to load autopilot settings'
        );
      }
    };

    loadAllData();
  }, [salesBotEnabled, updateSystemPrompt, updateAutopilotSettings]);

  useEffect(() => {
    if (salesBotEnabled === false) {
      navigate('/inbox');
    }
  }, [salesBotEnabled, navigate]);

  const handleToggle = async (value) => {
    await toast.promise(manageSalesBot(value ? 'enable' : 'disable'), {
      loading: `${value ? 'Enabling' : 'Disabling'} Autopilot...`,
      success: () => {
        setAutoPilotEnabled(value);
        return `Autopilot ${value ? 'enabled' : 'disabled'} successfully`;
      },
      error: (error) => {
        return (
          error.response?.data?.message || 'Failed to update Autopilot status'
        );
      },
    });
  };

  return (
    <div className='h-svh'>
      <div className='flex h-full flex-col'>
        {/* Header */}
        <div className='flex-shrink-0 border-b bg-background px-6 py-4'>
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-xl font-bold tracking-tight'>
                Revio Autopilot
              </h1>
              <p className='text-sm text-muted-foreground'>
                Configure and test your AI autopilot
              </p>
            </div>
            {autoPilotFeatureEnabled && (
              <div className='flex items-center gap-1 rounded-lg border bg-muted p-1'>
                <button
                  onClick={() => handleToggle(true)}
                  className={cn(
                    'rounded-md px-3 py-1 text-sm font-medium transition-colors duration-200',
                    autopilotEnabled
                      ? 'bg-green-600 text-white'
                      : 'text-muted-foreground hover:bg-muted/50'
                  )}
                >
                  Active
                </button>
                <button
                  onClick={() => handleToggle(false)}
                  className={cn(
                    'rounded-md px-3 py-1 text-sm font-medium transition-colors duration-200',
                    !autopilotEnabled
                      ? 'bg-background text-muted-foreground'
                      : 'text-muted-foreground hover:bg-muted/50'
                  )}
                >
                  Inactive
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className='border-b bg-background px-6 py-2'>
          <Tabs
            value={activeTab}
            onValueChange={(v) => {
              const selectedTab = tabs.find((tab) => tab.value === v);
              if (selectedTab) {
                navigate(selectedTab.path);
              } else {
                navigate('/revio-autopilot');
              }
            }}
            className='w-full'
          >
            <TabsList className='w-fit'>
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className='px-4 text-xs sm:text-sm'
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Content Area */}
        <div className='min-h-0 flex-1'>
          <ScrollArea className='h-full'>
            <div className='px-6'>
              <Outlet />
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

export default AutopilotLayout;
